import { HAS_SNOOZED_INSTALL_PROMPT, HAS_SNOOZED_INSTALL_PROMPT_COUNT } from 'constants/messages';
import type { UserPropertyEventDictionary } from 'redux/modules/user.types';
import { DOWNLOAD_MEMORY_TRACKER } from 'tracking/constants';
import { z } from 'zod';
import { HAS_ADDED_TIME_TO_HOUR, onboardingHints } from './hints/onboarding';
import { reportsHints } from './hints/reports';
export const DEFAULT_PROJECT_ID = 'default_project_id';
export const HIDE_TASK_SIDEBAR = 'hide_task_sidebar';
export const HAS_DOWNLOADED_MEMORY_TRACKER = 'has_downloaded_memory_tracker';
export const HAS_INSTALLED_MEMORY_TRACKER = 'has_installed_memory_tracker';
export const HAS_INSTALLED_DESKTOP_APP = 'has_installed_desktop_app';
export const HAS_CONNECTED_APP = 'has_connected_app';
export const HAS_SEEN_SUPERREPORTS = 'has_seen_superreports';
export const HAS_SEEN_CONTROL = 'has_seen_control';
export const HAS_CREATED_HOUR = 'has_created_hour';
export const HAS_CREATED_USER = 'has_created_user';
export const HAS_CREATED_PROJECT = 'has_created_project';
export const MEMORY_TOUR_STEP = 'memory_tour_step';
export const MEMORY_TOUR_COMPLETED = 'memory_tour_completed';
export const SHOW_HOUR_EDITOR_ATTACHMENTS = 'show_hour_editor_attachments';
export const SHOW_MEMORIES_ON_HOURS = 'show_memories_on_hours';
export const SHOW_MEMORY_DRAWER_CONTENT = 'show_memory_drawer_content';
export const HAS_SEEN_PEOPLE_INTEGRATIONS_MESSAGE = 'viewed_people_integrations_cta';
export const HAS_SEEN_PROJECTS_INTEGRATIONS_MESSAGE = 'viewed_projects_integrations_cta';

export const PREFERRED_AUTO_NOTE_STRATEGY = 'preferred_auto_note_strategy';
export const PREFERRED_PROJECTS_TAB = 'preferred_projects_tab';
export const PREFERRED_REPORT_VIEW = 'preferred_report_view';
export const PREFERRED_REPORT_FILTER = 'preferred_report_filter';
export const PREFERRED_CALENDAR_VIEW = 'preferred_calendar_view';
export const PREFERRED_HOURS_VIEW = 'preferred_hours_view';
export const PREFERRED_AUTOSHEET_VIEW = 'preferred_autosheet_view';
export const AutosheetViewSchema = z.union([z.literal('day'), z.literal('week')]);
export type AutosheetView = z.infer<typeof AutosheetViewSchema>;
export const PREFERRED_FORECASTS_VIEW_TYPE = 'preferred_forecasts_view_type';
export const PREFERRED_EXPORT_TYPE = 'preferred_export_type';

// TIMELINE FILTERS -- this seems like it might not scale well?
export const TIMELINE_mode = 'mode';
export const TIMELINE_pixelsPerMinute = 'minuteInPx';
export const TIMELINE_displayTimelineRules = 'displayTimelineRules';
export const TIMELINE_displayGroupRules = 'displayGroupRules';
export const LAST_VIEWED_CHANGELOG = 'last_viewed_changelog';
export const LAST_VIEWED_ANNOUNCEMENTS = 'last_viewed_announcements';
export const TIMELINE_sort = 'timelineSort';
export const IS_DIRECT_SALES_LEAD = 'is_direct_sales_lead';
export const ASSISTED_ONBOARDING_PHONE_NUMBER = 'assisted_onboarding_phone_number';
export const DONT_SHOW_BILLABLE_CHANGED_WARNING_AGAIN = 'dont_show_billable_changed';
export const HAS_DISMISSED_TASKS_TOUR = 'has_dismissed_tasks_tour';
export const HAS_DISMISSED_INCORRECT_TIMEZONE = 'has_dismissed_incorrect_timezone';
export const TIMESTAMP_SNAPPING_MINUTES = 'timestamp_snapping_minutes';
export const TIMELINE_FILTERS_TO_REMEMBER = [
  TIMELINE_mode,
  TIMELINE_pixelsPerMinute,
  TIMELINE_displayTimelineRules,
  TIMELINE_displayGroupRules,
  TIMELINE_sort,
] as const;

export const USERS_DASHBOARD_INCLUDE_PLANNED_TIME = 'users_dashboard_planned_time';
export const USERS_DASHBOARD_INCLUDE_TASKED_TIME = 'users_dashboard_tasked_time';

export const DONT_SHOW_OPEN_SEATS_WARNING_AGAIN = 'dont_show_open_seats_warning';
export const LAST_SEEN_ONBOARDING_STEP = 'last_seen_onboarding_step';

export const HIDE_SHORT_DURATION_MEMORY_TITLES = 'hide_short_memory_titles';
export const HAS_SEEN_AI_IGNORED_MEMORIES_HINT = 'has_seen_ai_ignored_hint';

export const HAS_ENABLED_TIC = 'has_enabled_tic';
export const TIC_AGREEMENT_SIGNED = 'signed_tic_agreement';
export const HIDE_TIC_AI_HINT = 'hide_tic_ai_hint';
export const HIDE_TIC_MIN_DURATION_HINT = 'hide_tic_min_duration_hint';
export const HIDE_TIC_LEGACY_MEMORIES_HINT = 'hide_tic_legacy_memories_hint';

// Onboarding banners for AI timesheet features
// IMPORTANT: User property keys must be ≤32 characters for database compatibility
// Current lengths: dismissed_timesheet_setup_done=30, dismissed_timesheet_gen_banner=30, dismissed_entry_expand_banner=29, dismissed_unassigned_banner=25
export const HAS_DISMISSED_TIMESEET_SETUP_DONE = 'dismissed_timesheet_setup_done';
export const HAS_DISMISSED_FIRST_TIMESHEET_GENERATION = 'dismissed_timesheet_gen_banner';
export const HAS_DISMISSED_FIRST_ENTRY_EXPANSION = 'dismissed_entry_expand_banner';
export const HAS_DISMISSED_FIRST_UNASSIGNED_EXPANSION = 'dismissed_unassigned_banner';
export const HAS_DISMISSED_GOOGLE_CALENDAR_BANNER = 'dismissed_gcal_banner';
export const DISMISSED_TIC_ID_BANNERS = 'dismissed_tic_id_banners';
export const HAS_SUPPRESSED_UNSUBMITTED_MODAL = 'suppress_unsubmitted_modal';
// TT-5819: the user chose to log time manually on AutoSheet instead of installing Memory.
// The blocking download prompt is replaced by a degraded-experience warning. (23 chars, ≤32 limit.)
export const HAS_DISMISSED_MEMORY_DOWNLOAD_PROMPT = 'dismissed_memory_prompt';

// Separate viewmode between classic and tic
// We do this because we don't want people who have been on tic for a while to suddenly
// be thrown back to list view just because we add the tic list view.
export const TIC_MEMORY_VIEWMODE = 'memory_viewmode';
export const TicMemoryViewmodeSchema = z.union([z.literal('timeline'), z.literal('list')]);
export type TicMemoryViewmode = z.infer<typeof TicMemoryViewmodeSchema>;

export const TIC_SHOW_COMPLETED_TASKS = 'tic_show_completed_tasks';
export const TIC_SHOW_ACTIVE_TASKS = 'tic_show_active_tasks';
// Legacy #6767 "only show tasks from selected project" toggle. Superseded by
// TIC_LINKED_WORK_SCOPE, but kept as the read-only fallback source so users who already
// enabled it keep the 'project' scope until they pick something in the new control.
export const TIC_FILTER_BY_PROJECT = 'tic_filter_by_project';
// TT-4408: linked-work relevance scope (all | integration | client | project), persisted as a
// user property. Mutually-exclusive single choice; 'all' is stored explicitly (never as false).
export const TIC_LINKED_WORK_SCOPE = 'tic_linked_work_scope';
// TT-4408 PR2: assigned-to-me relevance filter. Declared now; the UI + filtering are
// commented out until the scoped-task backend lands (assignedToMe query param +
// assigned_to_me response field), at which point this is re-enabled.
export const TIC_LINKED_WORK_ASSIGNED_TO_ME = 'tic_linked_work_assigned_me';
export const CLIENT_FOCUS_ON_PROJECTS_BY_DEFAULT = 'client_focus_on_projects_default';

// Tic demo mode settings
export const TIC_DEMO_MODE_SETTINGS = 'tic_demo_mode_settings';

// TT-5146: timesheet delivery cadence per workspace, set from the compliance
// dashboard's policy settings. Value is a `accountId=freq:day:coverage` map —
// see serializeCadenceMap in routes/users/components/compliance/derive.ts.
// Stored as a user property while the setting is validated; promote to a
// backend workspace setting once it sticks. (27 chars, ≤32 limit.)
export const COMPLIANCE_DELIVERY_CADENCE = 'compliance_delivery_cadence';

export const TIC_ONBOARDING_HINTS = [
  HIDE_TIC_AI_HINT,
  HIDE_TIC_MIN_DURATION_HINT,
  HIDE_TIC_LEGACY_MEMORIES_HINT,
  HAS_DISMISSED_TIMESEET_SETUP_DONE,
  HAS_DISMISSED_FIRST_TIMESHEET_GENERATION,
  HAS_DISMISSED_FIRST_ENTRY_EXPANSION,
  HAS_DISMISSED_FIRST_UNASSIGNED_EXPANSION,
  HAS_DISMISSED_GOOGLE_CALENDAR_BANNER,
  DISMISSED_TIC_ID_BANNERS,
  HAS_SUPPRESSED_UNSUBMITTED_MODAL,
  HAS_DISMISSED_MEMORY_DOWNLOAD_PROMPT,
] as const;

// Whitelist of all user properties we'll allow to be set.
// The whitelists helps avoid misspellings
// NOTE: Also see user.types for union type AllAcceptedUserPropertyKey inferred from this array
export const ACCEPTED_USER_PROPERTIES = [
  TIC_MEMORY_VIEWMODE,
  DEFAULT_PROJECT_ID,
  HIDE_TASK_SIDEBAR,
  HAS_DOWNLOADED_MEMORY_TRACKER,
  HAS_INSTALLED_MEMORY_TRACKER,
  HAS_INSTALLED_DESKTOP_APP,
  HAS_CONNECTED_APP,
  HAS_SEEN_SUPERREPORTS,
  HAS_SEEN_CONTROL,
  HAS_CREATED_HOUR,
  HAS_CREATED_USER,
  HAS_CREATED_PROJECT,
  MEMORY_TOUR_STEP,
  MEMORY_TOUR_COMPLETED,
  SHOW_HOUR_EDITOR_ATTACHMENTS,
  SHOW_MEMORIES_ON_HOURS,
  SHOW_MEMORY_DRAWER_CONTENT,
  PREFERRED_AUTO_NOTE_STRATEGY,
  PREFERRED_PROJECTS_TAB,
  PREFERRED_REPORT_VIEW,
  PREFERRED_REPORT_FILTER,
  PREFERRED_CALENDAR_VIEW,
  PREFERRED_FORECASTS_VIEW_TYPE,
  PREFERRED_EXPORT_TYPE,
  PREFERRED_HOURS_VIEW,
  PREFERRED_AUTOSHEET_VIEW,
  HAS_SNOOZED_INSTALL_PROMPT,
  HAS_SNOOZED_INSTALL_PROMPT_COUNT,
  HAS_SEEN_PEOPLE_INTEGRATIONS_MESSAGE,
  HAS_SEEN_PROJECTS_INTEGRATIONS_MESSAGE,
  LAST_VIEWED_CHANGELOG,
  LAST_VIEWED_ANNOUNCEMENTS,
  DONT_SHOW_OPEN_SEATS_WARNING_AGAIN,
  ...onboardingHints,
  ...reportsHints,
  ...TIMELINE_FILTERS_TO_REMEMBER,
  IS_DIRECT_SALES_LEAD,
  ASSISTED_ONBOARDING_PHONE_NUMBER,
  DONT_SHOW_BILLABLE_CHANGED_WARNING_AGAIN,
  HAS_DISMISSED_TASKS_TOUR,
  HAS_DISMISSED_INCORRECT_TIMEZONE,
  LAST_SEEN_ONBOARDING_STEP,
  USERS_DASHBOARD_INCLUDE_PLANNED_TIME,
  USERS_DASHBOARD_INCLUDE_TASKED_TIME,
  HIDE_SHORT_DURATION_MEMORY_TITLES,
  HAS_SEEN_AI_IGNORED_MEMORIES_HINT,
  HAS_ENABLED_TIC,
  TIC_AGREEMENT_SIGNED,
  ...TIC_ONBOARDING_HINTS,
  TIMESTAMP_SNAPPING_MINUTES,
  TIC_DEMO_MODE_SETTINGS,
  TIC_SHOW_COMPLETED_TASKS,
  TIC_SHOW_ACTIVE_TASKS,
  TIC_FILTER_BY_PROJECT,
  TIC_LINKED_WORK_SCOPE,
  CLIENT_FOCUS_ON_PROJECTS_BY_DEFAULT,
  COMPLIANCE_DELIVERY_CADENCE,
  // TT-4408: accepted now so the inert assigned-to-me selector plumbing type-checks;
  // the UI, query param, and filtering are activated in PR2 with the backend.
  TIC_LINKED_WORK_ASSIGNED_TO_ME,
] as const;

// Add hints by:
// - Naming a new export
// - Adding it to USER_HINTS list
// - Adding it to HINT_META_DATA
// - Update the target component to include id="hint-whatever"
//
// Hint structure
//
// [HINT_KEY]: {
//   name: 'Some name',
//   description: 'A description'
//   hideUntil: [
//     KEY_NAMES_THAT_MUST_BE_SEEN_BEFORE_THIS_HINT,
//   ],

//   // Number & array values only
//   direction: 'bottom-right', // left, top, bottom -- simple aliases for modal origin.
//   delay: numIsMs, // defaults to instantly show
// },

// NOTE: Also se user.types for union type inferred from this array
export const USER_PROPERTY_TRACKING_BLACKLIST = [
  SHOW_HOUR_EDITOR_ATTACHMENTS,
  HAS_ADDED_TIME_TO_HOUR,
  SHOW_MEMORIES_ON_HOURS,
  SHOW_MEMORY_DRAWER_CONTENT,
  HAS_DOWNLOADED_MEMORY_TRACKER,
  HAS_CONNECTED_APP,
  DEFAULT_PROJECT_ID,
  HAS_INSTALLED_MEMORY_TRACKER,
  DOWNLOAD_MEMORY_TRACKER,
  TIMELINE_pixelsPerMinute,
  MEMORY_TOUR_STEP,
  HAS_SNOOZED_INSTALL_PROMPT,
  HAS_SNOOZED_INSTALL_PROMPT_COUNT,
  HAS_SEEN_CONTROL,
  TIMELINE_displayTimelineRules,
  TIMELINE_displayGroupRules,
  HAS_SEEN_PEOPLE_INTEGRATIONS_MESSAGE,
  HAS_SEEN_PROJECTS_INTEGRATIONS_MESSAGE,
  DONT_SHOW_OPEN_SEATS_WARNING_AGAIN,
  ...TIC_ONBOARDING_HINTS,
] as const;

export const USER_PROPERTIES_EVENT_NAMES: UserPropertyEventDictionary = {
  mode: 'Set Timeline Mode',
  memory_tour_completed: 'Completed Memory Tour',
  has_seen_unlinked_memories: 'First Unlogged Memory Editor',
  has_seen_suggested_hour: 'First Suggested Hour',
  has_linked_memory_to_project: 'Linked First Memory To Project',
  has_filtered_reports: '',
  has_added_widget_to_report: 'Added First Widget To Report',
  has_seen_can_reorder: 'First saw that columns can be re-ordered in reports',
  has_seen_superreports: 'First Viewed Reports',
  has_installed_desktop_app: 'First Open Desktop App',
  preferred_projects_tab: 'Set Preferred Projects Tab',
  preferred_report_view: 'Set Preferred Report View',
  preferred_report_filter: 'Set Preferred Report Filter',
  has_created_hour: 'Log Your First Hour',
  has_created_project: 'Create Your First Project',
  has_created_user: 'Invite Your First User',
  last_viewed_changelog: 'Viewed changelog',
  last_viewed_announcements: 'Viewed announcements',
  preferred_export_type: 'Set Preferred Export Type',
  [HAS_ENABLED_TIC]: 'Enabled TIC',
  [TIC_AGREEMENT_SIGNED]: 'Signed TIC Agreement',
};
